import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { isAnalyticsUser } from '../config/api';

const Dashboard: React.FC = () => {
  const navigate = useNavigate();

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ignore if typing in input
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      // Ignore if any modifier key is pressed (Cmd, Ctrl, Alt, Shift)
      if (e.metaKey || e.ctrlKey || e.altKey || e.shiftKey) return;

      switch (e.key.toLowerCase()) {
        case 'v':
          navigate('/videos');
          break;
        case 'b':
          navigate('/bulk-review');
          break;
        case 'r':
          navigate('/reports');
          break;
        case 'n':
          navigate('/notifications');
          break;
        case 'c':
          navigate('/campaigns');
          break;
        case 'm':
          navigate('/image-moderation');
          break;
        case 'e':
          navigate('/exclusive-members');
          break;
        case 'l':
          navigate('/live-access');
          break;
        case 'a':
          if (isAnalyticsUser()) navigate('/analytics');
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [navigate]);

  // PingTop brand gradient
  const brandGradient = 'from-[#FF9C15] to-[#F42026]';

  const showAnalytics = isAnalyticsUser();

  const menuItems = [
    {
      title: 'Live Quest Growth Review',
      description: 'Compare XP, Token funding, gift transfers, ledger reconciliation and cashout status for two accounts before making a decision',
      icon: <svg className='w-12 h-12' fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={1.5} d='M3 3h18v18H3V3zm3 12 4-4 3 2 4-5' /></svg>,
      path: '/growth-audit',
      stats: 'Read-only account review',
    },
    {
      title: 'Live Review',
      description: 'Watch current livestreams, inspect Gemini findings and comments, and check review coverage',
      icon: (
        <svg className='w-12 h-12' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
          <rect x='3' y='6' width='12' height='12' rx='2' strokeWidth={1.5} />
          <path d='M15 10l6-3v10l-6-3' strokeWidth={1.5} strokeLinejoin='round' />
        </svg>
      ),
      path: '/live-review',
      stats: 'Live monitoring · AI observations',
    },
    ...(showAnalytics ? [{
      title: 'Analytics',
      description: 'Real-time analytics dashboard with user growth, video uploads, orders, geographic data, and more',
      icon: (
        <svg className='w-12 h-12' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
          <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={1.5} d='M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z' />
        </svg>
      ),
      path: '/analytics',
      stats: 'Live data',
    }] : []),
    {
      title: 'Video Review',
      description: 'Review videos one by one with detailed controls and actions',
      icon: (
        <svg className='w-12 h-12' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
          <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={1.5} d='M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z' />
          <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={1.5} d='M21 12a9 9 0 11-18 0 9 9 0 0118 0z' />
        </svg>
      ),
      path: '/videos',
      stats: 'Single video view',
    },
    {
      title: 'Bulk Review',
      description: 'Review multiple videos at once in a grid layout for faster moderation',
      icon: (
        <svg className='w-12 h-12' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
          <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={1.5} d='M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z' />
        </svg>
      ),
      path: '/bulk-review',
      stats: 'Grid view',
    },
    {
      title: 'Reports',
      description: 'View and manage user reports for videos, comments, and other content',
      icon: (
        <svg className='w-12 h-12' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
          <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={1.5} d='M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' />
        </svg>
      ),
      path: '/reports',
      stats: 'User reports',
    },
    {
      title: 'Notification Analytics',
      description: 'Track push notification performance, click rates, resurrection metrics, and campaign effectiveness',
      icon: (
        <svg className='w-12 h-12' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
          <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={1.5} d='M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9' />
        </svg>
      ),
      path: '/notifications',
      stats: 'Campaign analytics',
    },
    {
      title: 'Campaigns',
      description: 'Create and manage marketing notification campaigns for user engagement and re-engagement',
      icon: (
        <svg className='w-12 h-12' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
          <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={1.5} d='M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z' />
          <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={1.5} d='M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z' />
        </svg>
      ),
      path: '/campaigns',
      stats: 'Campaign management',
    },
    {
      title: 'AI Image Moderation',
      description: 'Monitor AI-powered image moderation, track block rates, and identify repeat violators in real-time',
      icon: (
        <svg className='w-12 h-12' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
          <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={1.5} d='M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z' />
        </svg>
      ),
      path: '/image-moderation',
      stats: 'Real-time analytics',
    },
    {
      title: 'Exclusive Members',
      description: 'Review creator applications, approve pending requests, and monitor active exclusive creators',
      icon: (
        <svg className='w-12 h-12' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
          <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={1.5} d='M17 20h5V4H2v16h5m10 0v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6m10 0H7' />
          <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={1.5} d='M12 11a3 3 0 100-6 3 3 0 000 6z' />
        </svg>
      ),
      path: '/exclusive-members',
      stats: 'Creator approvals',
    },
    {
      title: 'Live Access',
      description: 'Review live access applications, approve creators, and control the production access mode and allowlist',
      icon: (
        <svg className='w-12 h-12' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
          <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={1.5} d='M5 12l5 5L20 7' />
          <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={1.5} d='M12 22C6.477 22 2 17.523 2 12S6.477 2 12 2s10 4.477 10 10-4.477 10-10 10z' />
        </svg>
      ),
      path: '/live-access',
      stats: 'Go live control',
    },
  ];

  return (
    <div className='min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900'>
      <Navbar />

      {/* Hero Section */}
      <div className='relative overflow-hidden'>
        {/* Background decoration */}
        <div className='absolute inset-0 overflow-hidden pointer-events-none'>
          <div className='absolute -top-40 -right-40 w-80 h-80 bg-[#FF9C15]/10 rounded-full blur-3xl' />
          <div className='absolute -bottom-40 -left-40 w-80 h-80 bg-[#F42026]/10 rounded-full blur-3xl' />
          <div className='absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-[#FF9C15]/5 rounded-full blur-3xl' />
        </div>

        <div className='relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-20'>
          {/* Header */}
          <div className='text-center mb-12 sm:mb-16'>
            <div className='inline-flex items-center gap-2 px-4 py-2 bg-white/5 backdrop-blur-sm rounded-full border border-white/10 mb-6'>
              <div className='w-2 h-2 bg-green-400 rounded-full animate-pulse' />
              <span className='text-sm text-gray-300'>Content Moderation System</span>
            </div>
            <h1 className='text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4'>
              <span className='bg-gradient-to-r from-[#FF9C15] to-[#F42026] bg-clip-text text-transparent'>
                PingTop
              </span>{' '}
              Moderation
            </h1>
            <p className='text-lg sm:text-xl text-gray-400 max-w-2xl mx-auto'>
              Manage and review content across the platform with powerful moderation tools
            </p>
          </div>

          {/* Menu Cards */}
          <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8'>
            {menuItems.map((item) => (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                className='group relative bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 sm:p-8 text-left transition-all duration-300 hover:bg-white/10 hover:border-[#FF9C15]/30 hover:scale-[1.02] hover:shadow-2xl hover:shadow-[#F42026]/10'
              >
                {/* Gradient overlay on hover */}
                <div className={`absolute inset-0 bg-gradient-to-br ${brandGradient} opacity-0 group-hover:opacity-10 rounded-2xl transition-opacity duration-300`} />

                {/* Icon */}
                <div className={`inline-flex p-4 bg-gradient-to-br ${brandGradient} rounded-xl text-white mb-6 shadow-lg`}>
                  {item.icon}
                </div>

                {/* Content */}
                <h2 className='text-xl sm:text-2xl font-bold text-white mb-3 group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:bg-clip-text group-hover:from-[#FF9C15] group-hover:to-[#F42026] transition-all duration-300'>
                  {item.title}
                </h2>
                <p className='text-gray-400 text-sm sm:text-base mb-4 leading-relaxed'>
                  {item.description}
                </p>

                {/* Stats badge */}
                <div className='inline-flex items-center gap-2 px-3 py-1.5 bg-white/5 rounded-full'>
                  <div className={`w-1.5 h-1.5 bg-gradient-to-r ${brandGradient} rounded-full`} />
                  <span className='text-xs text-gray-400'>{item.stats}</span>
                </div>

                {/* Arrow indicator */}
                <div className='absolute bottom-6 right-6 sm:bottom-8 sm:right-8 opacity-0 group-hover:opacity-100 transform translate-x-2 group-hover:translate-x-0 transition-all duration-300'>
                  <svg className='w-6 h-6 text-[#FF9C15]' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                    <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M17 8l4 4m0 0l-4 4m4-4H3' />
                  </svg>
                </div>
              </button>
            ))}
          </div>

          {/* Quick Stats / Info */}
          <div className='mt-12 sm:mt-16 grid grid-cols-2 md:grid-cols-8 gap-4'>
            {[
              { label: 'Video Review', value: 'Single', icon: '🎬' },
              { label: 'Bulk Review', value: 'Grid', icon: '📱' },
              { label: 'Reports', value: 'Manage', icon: '📊' },
              { label: 'Notifications', value: 'Analytics', icon: '🔔' },
              { label: 'Campaigns', value: 'Manage', icon: '📣' },
              { label: 'AI Moderation', value: 'Live', icon: '🛡️' },
              { label: 'Exclusive', value: 'Review', icon: '⭐' },
              { label: 'Status', value: 'Online', icon: '🟢' },
            ].map((stat) => (
              <div key={stat.label} className='bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4 text-center'>
                <div className='text-2xl mb-2'>{stat.icon}</div>
                <div className='text-lg font-semibold text-white'>{stat.value}</div>
                <div className='text-xs text-gray-500'>{stat.label}</div>
              </div>
            ))}
          </div>

          {/* Keyboard shortcuts hint (desktop only) */}
          <div className='hidden lg:block mt-12 text-center'>
            <div className='inline-flex items-center gap-4 px-6 py-3 bg-white/5 backdrop-blur-sm border border-white/10 rounded-full'>
              <span className='text-sm text-gray-400'>Quick navigation:</span>
              <div className='flex items-center gap-2'>
                <kbd className='px-2 py-1 bg-white/10 rounded text-xs text-gray-300 font-mono'>V</kbd>
                <span className='text-xs text-gray-500'>Videos</span>
              </div>
              <div className='flex items-center gap-2'>
                <kbd className='px-2 py-1 bg-white/10 rounded text-xs text-gray-300 font-mono'>B</kbd>
                <span className='text-xs text-gray-500'>Bulk</span>
              </div>
              <div className='flex items-center gap-2'>
                <kbd className='px-2 py-1 bg-white/10 rounded text-xs text-gray-300 font-mono'>R</kbd>
                <span className='text-xs text-gray-500'>Reports</span>
              </div>
              <div className='flex items-center gap-2'>
                <kbd className='px-2 py-1 bg-white/10 rounded text-xs text-gray-300 font-mono'>N</kbd>
                <span className='text-xs text-gray-500'>Notifications</span>
              </div>
              <div className='flex items-center gap-2'>
                <kbd className='px-2 py-1 bg-white/10 rounded text-xs text-gray-300 font-mono'>C</kbd>
                <span className='text-xs text-gray-500'>Campaigns</span>
              </div>
              <div className='flex items-center gap-2'>
                <kbd className='px-2 py-1 bg-white/10 rounded text-xs text-gray-300 font-mono'>M</kbd>
                <span className='text-xs text-gray-500'>Moderation</span>
              </div>
              <div className='flex items-center gap-2'>
                <kbd className='px-2 py-1 bg-white/10 rounded text-xs text-gray-300 font-mono'>E</kbd>
                <span className='text-xs text-gray-500'>Exclusive</span>
              </div>
              <div className='flex items-center gap-2'>
                <kbd className='px-2 py-1 bg-white/10 rounded text-xs text-gray-300 font-mono'>L</kbd>
                <span className='text-xs text-gray-500'>Live Access</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
